import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { formatTimeframe } from '@/features/rapidstor/helpers';
import { isDealCustomHeading } from '@/core/utils/dealDisplay';
import { useDealDisplay } from '@/journey-based-listing/hooks/useDealDisplay';

import type { DealSelectorProps } from '../OptionSelector';

import classes from './DealRibbon.module.scss';

const cardVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    tap: { scale: 0.97 },
};

function DealRibbon({
    deals,
    noDealOption,
    onSelectDeal,
    onClearDeal,
    location,
}: DealSelectorProps) {
    const { t } = useTranslation();
    const { getDealPriceDisplay } = useDealDisplay();

    if (deals.length === 0 && !noDealOption.show) {
        return null;
    }

    return (
        <div className={classes.ribbon}>
            {noDealOption.show && (
                <motion.button
                    type="button"
                    className={`${classes.card} ${noDealOption.isActive ? classes.active : ''}`}
                    variants={cardVariants}
                    initial="initial"
                    animate="animate"
                    whileTap="tap"
                    transition={{ duration: 0.2 }}
                    onClick={onClearDeal}
                >
                    {noDealOption.isActive && (
                        <span className={classes.checkmark}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="7" cy="7" r="7" fill="var(--rs-theme)" />
                                <path d="M4 7L6 9L10 5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    )}
                    <span className={classes.title}>
                        {t('deal_selector:standard_rate', 'Standard Rate')}
                    </span>
                    <span className={classes.rate}>
                        {formatPriceLabel(formatCurrency(noDealOption.rate, location), location, false, true)}
                    </span>
                    <span className={classes.period}>
                        {formatTimeframe(location)}
                    </span>
                </motion.button>
            )}

            {deals.map((dealOption) => (
                <motion.button
                    type="button"
                    key={dealOption.deal._id}
                    className={`${classes.card} ${dealOption.isActive ? classes.active : ''}`}
                    variants={cardVariants}
                    initial="initial"
                    animate="animate"
                    whileTap="tap"
                    transition={{ duration: 0.2 }}
                    onClick={() => onSelectDeal(dealOption.deal._id)}
                >
                    {dealOption.isActive && (
                        <span className={classes.checkmark}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="7" cy="7" r="7" fill="var(--rs-theme)" />
                                <path d="M4 7L6 9L10 5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    )}
                    <span className={classes.title}>
                        {dealOption.deal.title}
                    </span>
                    <span className={classes.rate}>
                        {getDealPriceDisplay(dealOption.deal, dealOption.rate, true)}
                    </span>
                    {!isDealCustomHeading(dealOption.deal) && (
                        <span className={classes.period}>
                            {formatTimeframe(location)}
                        </span>
                    )}
                </motion.button>
            ))}
        </div>
    );
}

export default DealRibbon;
